<template>
  <div class="property-title">{{ priorityType }} Priorities:</div>
  <div v-if="priorityObjects?.length" class="priorities-table">
    <div
      v-for="item in priorityObjects"
      :key="item.getName()"
      class="priority-row"
    >
      <span class="priority-index">{{ priorityObjects.indexOf(item) + 1 }}</span>
      <v-tooltip :text="item.getName()" location="top" open-delay="500">
        <template #activator="{ props: tooltipProps }">
          <span v-bind="tooltipProps" class="priority-name">{{ item.getName() }}</span>
        </template>
      </v-tooltip>
      <span class="priority-actions">
        <v-btn
          icon="mdi-arrow-up-thin"
          size="x-small"
          variant="plain"
          :disabled="firstPrio(item) || PreferenceStore.INSTANCE.isReadOnly"
          @click="prioUp(item)"
        />
        <v-btn
          icon="mdi-arrow-down-thin"
          size="x-small"
          variant="plain"
          :disabled="lastPrio(item) || PreferenceStore.INSTANCE.isReadOnly"
          @click="prioDown(item)"
        />
      </span>
    </div>
  </div>
  <div v-else>No {{ priorityType }}s defined.</div>
</template>

<script setup lang="ts">
import { Transition } from "../mvc/transition";
import { Region } from "../mvc/region";
import { Statechart } from "../mvc/statechart";
import { computed, inject } from "vue";
import { ModelEventBus } from "../../event-bus";
import { BaseNode } from "../mvc/base/base-node";
import RappidContext from "../rappid/rappid-context";
import { PreferenceStore } from "../services/preference-store";

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");
const compProps = defineProps<{ model: BaseNode | Statechart }>();

const priorityObjects = computed(() => {
  if ("getOutgoingTransitions" in compProps.model) {
    // Transition for BaseNodes
    return compProps.model.getOutgoingTransitions();
  } else {
    // Region for Statecharts
    return compProps.model.getTopLevelRegions();
  }
});

const priorityType = computed(() => {
  if ("getOutgoingTransitions" in compProps.model) {
    return "Transition";
  } else {
    return "Region";
  }
});

function firstPrio(item) {
  return priorityObjects.value.indexOf(item) < 1;
}

function lastPrio(item) {
  return priorityObjects.value.indexOf(item) + 1 >= priorityObjects.value.length;
}

function prioUp(priorityObject) {
  const currentIndex = priorityObjects.value.indexOf(priorityObject);

  const tempTransitions = [...priorityObjects.value];

  // Can't move up if it's at the top or out of bounds
  if (currentIndex <= 0 || currentIndex >= tempTransitions.length) {
    return;
  }
  // Swap the current item with the previous one
  [tempTransitions[currentIndex - 1], tempTransitions[currentIndex]] = [
    tempTransitions[currentIndex],
    tempTransitions[currentIndex - 1],
  ];

  updateTransitions(tempTransitions);
}

function prioDown(priorityObject) {
  const currentIndex = priorityObjects.value.indexOf(priorityObject);

  const tempTransitions = [...priorityObjects.value];

  // Can't move down if it's at the bottom or out of bounds
  if (currentIndex < 0 || currentIndex >= tempTransitions.length - 1) {
    return;
  }
  // Swap the current item with the next one
  [tempTransitions[currentIndex + 1], tempTransitions[currentIndex]] = [
    tempTransitions[currentIndex],
    tempTransitions[currentIndex + 1],
  ];

  updateTransitions(tempTransitions);
}

function updateTransitions(tempTransitions) {
  rappidContext.commandManager.initBatchCommand();
  priorityObjects.value.forEach((t: Transition | Region) => {
    t.index(tempTransitions.indexOf(t) + 1);
  });
  rappidContext.commandManager.storeBatchCommand();
  ModelEventBus.emit("prioritiesChanged");
}
</script>

<style>
.priorities-table {
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  padding: 2px 0;
}
.v-theme--dark .priorities-table {
  border-color: rgba(255, 255, 255, 0.38);
}
.priority-row {
  display: flex;
  align-items: center;
  padding: 0 4px;
  min-height: 28px;
}
.priority-index {
  min-width: 18px;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.5);
  text-align: right;
  margin-right: 8px;
}
.v-theme--dark .priority-index {
  color: rgba(255, 255, 255, 0.5);
}
.priority-name {
  flex: 1;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.priority-actions {
  display: flex;
  gap: 0;
  flex-shrink: 0;
}
</style>
